/* eslint-disable */
import type { NavigationGuardNext, Route } from 'vue-router';
import { getGlobalAuthentication } from '@rready/security';
import { AppNavigation } from '@rready/integration';
import { resolveContextDomain, resolveContextId } from '@rready/composables';
import { useAuthStore } from '../../store/authStore';
import { resolveContext, resolveProduct } from './resolver';
import { useContextStore } from '../../store/contextStore';

export function logoutGuard(to: Route, from: Route, next: NavigationGuardNext) {
  if (to.name === 'LogoutPage') {
    const url = AppNavigation.authLogoutUrl();
    window.location.assign(url);
  } else {
    next();
  }
}

export function authGuard(to: Route, from: Route, next: NavigationGuardNext) {
  const environment = process.env.VUE_APP_ENV_NAME ?? '';
  const token = getGlobalAuthentication(environment);
  useAuthStore().updateToken(token ?? '');

  const authenticated = useAuthStore().authenticated;
  if (!authenticated) {
    const contextDomain = resolveContextDomain(window.location);
    const url = AppNavigation.authLoginUrl(
      'portal',
      contextDomain,
      to.fullPath
    );
    window.location.replace(url);
  } else {
    window.scrollTo({ top: 0 });
    next();
  }
}

export function resolveContextGuard(
  to: Route,
  from: Route,
  next: NavigationGuardNext
) {
  const contextDomain = resolveContextDomain(window.location);
  const currentContext = useContextStore().contextId;
  // Because when switching from domain A should not allow context B, the context should stay be A
  const isCorrectSubdomain =
    !currentContext ||
    [currentContext.toLowerCase(), 'app'].includes(contextDomain);

  if (isCorrectSubdomain && !to.query.context && currentContext) {
    next();
    return;
  }

  const invitedContexts = useAuthStore().token?.contexts ?? [];
  const contextId =
    resolveContextId(contextDomain, invitedContexts) ??
    to.query.context?.toString();

  if (!isCorrectSubdomain && contextId) {
    const portalUrl = AppNavigation.portalUrl(contextDomain, 'app');
    const url = new URL(to.fullPath, portalUrl);
    url.searchParams.set('context', contextId);
    window.location.replace(url);
  }

  if (contextId) {
    useContextStore().updateContext(contextId);
    next();
  } else if (to.name !== 'AppError') {
    next({
      name: 'AppError',
      params: {
        errorCode: 'invalid-context'
      }
    });
  } else {
    next();
  }
}

export function fallbackGuard(
  to: Route,
  from: Route,
  next: NavigationGuardNext
) {
  next('/');
}

export function compatibilityGuard(
  to: Route,
  from: Route,
  next: NavigationGuardNext
) {
  const autoProduct = '_';
  if (to.fullPath.startsWith('/services')) {
    console.log(`[COMPATIBILITY] ${to.fullPath}`);
    next({ name: 'ServiceOverview', params: { product: autoProduct } });
  } else if (to.fullPath.startsWith('/tickets')) {
    console.log(`[COMPATIBILITY] ${to.fullPath}`);
    next({
      name: 'TicketOverview',
      params: { product: autoProduct }
    });
  } else if (to.fullPath.startsWith('/create-ticket')) {
    console.log(`[COMPATIBILITY] ${to.fullPath}`);
    const { searchParams, pathname, hash } = new URL(window.location.href);
    const query = Object.fromEntries(searchParams.entries());
    next({
      name: 'CreateTicket',
      params: { product: autoProduct },
      query,
      hash
    });
  } else if (to.fullPath.startsWith('/people')) {
    console.log(`[COMPATIBILITY] ${to.fullPath}`);
    next({ name: 'PeopleOverview', params: { product: autoProduct } });
  } else if (to.fullPath.startsWith('/profile/edit')) {
    console.log(`[COMPATIBILITY] ${to.fullPath}`);
    next({ name: 'EditProfile', params: { product: autoProduct } });
  } else if (to.fullPath.startsWith('/ticket/')) {
    console.log(`[COMPATIBILITY] ${to.fullPath}`);
    const { searchParams, pathname, hash } = new URL(window.location.href);
    const query = Object.fromEntries(searchParams.entries());
    const ticketId = pathname.split('/')[2];

    next({
      name: 'TicketDetails',
      params: { product: autoProduct, id: ticketId },
      query,
      hash
    });
  } else if (to.fullPath.startsWith('/edit-ticket')) {
    console.log(`[COMPATIBILITY] ${to.fullPath}`);
    const ticketId = to.fullPath.split('/')[2];
    next({
      name: 'EditTicket',
      params: {
        product: autoProduct,
        id: ticketId
      }
    });
  } else {
    next();
  }
}

export function completeProfileGuard(
  to: Route,
  from: Route,
  next: NavigationGuardNext
) {
  const PROFILE_PAGE_NAME = 'CompleteProfile';
  const LOGOUT_PAGE_NAME = 'LogoutPage';
  const product = resolveProduct()!;

  if (to.name === PROFILE_PAGE_NAME) {
    next({ params: { product } });
    return;
  }
  const user = useAuthStore().user;
  if (to.name !== LOGOUT_PAGE_NAME && user && !user.profileCompleted()) {
    next({
      name: PROFILE_PAGE_NAME,
      params: { product }
    });
  } else {
    next();
  }
}

export function preSubdomainGuard(
  to: Route,
  from: Route,
  next: NavigationGuardNext
) {
  const subdomain = resolveContextDomain(window.location);

  if ('admin' === subdomain) {
    const resolved = resolveContext();
    if (!resolved) {
      const url = AppNavigation.authCompanyUrl(subdomain, to.fullPath);
      window.location.replace(url);
    } else {
      const portalUrl = AppNavigation.adminUrl(resolved);
      const url = new URL(to.fullPath, portalUrl);
      window.location.replace(url);
    }
  } else if (to.query.context) {
    // Redirect to the correct subdomain for special contexts
    switch (to.query.context) {
      case 'serviceProvider':
        window.location.replace(AppNavigation.marketplaceUrl());
        break;
      case 'meta':
        window.location.replace(AppNavigation.metaUrl());
        break;
      default:
        next();
    }
  } else {
    next();
  }
}
